import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store/store";

export interface CounterState {
  showBtnBack: boolean;
  backToPath: string;
  title: string;
  loading: boolean;
  cmt_fullname: string;
  cmt_token: string;
  currentUser: string;
  isRootAdmin: boolean;
  isLogged: boolean;
  user: any; // Add user object to store admin data
}

const initialState: CounterState = {
  showBtnBack: false,
  backToPath: "",
  title: "",
  loading: false,
  cmt_fullname: "",
  cmt_token: "",
  isRootAdmin: false, // Set to false since this is admin system, not root admin
  currentUser: localStorage.getItem("cmt_fullname") || "",
  isLogged: localStorage.getItem("cmt_token") ? true : false,
  user: localStorage.getItem("cmt_token") ? { 
    requirePasswordChange: localStorage.getItem("cmt_require_password_change") === "true" 
  } : null, // Initialize user with requirePasswordChange from localStorage if logged in
};

export const appStore = createSlice({
  name: "app",
  initialState,
  reducers: {
    setTitleWithBack: (state, action: any) => {
      state.showBtnBack = true;
      state.title = action.payload.title;
      state.backToPath = action.payload.path;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.showBtnBack = false;
      state.backToPath = "";
      state.title = action.payload;
    },
    setUser: (state, action: any) => {
      const payload = action.payload;
      const fullname = `${payload.firstname} ${payload.lastname}`;
      state.cmt_fullname = fullname;
      state.cmt_token = payload.token;
      localStorage.setItem("cmt_token", payload.token);
      localStorage.setItem("cmt_fullname", fullname);
      
      // Store requirePasswordChange flag in localStorage
      if (payload.requirePasswordChange) {
        localStorage.setItem("cmt_require_password_change", "true");
      } else {
        localStorage.removeItem("cmt_require_password_change");
      }
      
      // Safely check if user is root admin (role ID = 1)
      const adminRoles = payload.adminRoles || [];
      const isRoot = adminRoles.some((role: any) => role.id === 1);
      state.isRootAdmin = isRoot;
      
      if (isRoot) {
        localStorage.setItem("cmt_admin", "1");
      }
      // Store complete user data including requirePasswordChange
      state.user = payload;
    },
    setProfile: (state, action: any) => {
      const payload = action.payload;
      if (payload.message === "error") {
        state.isRootAdmin = false;
      } else {
        // Safely check if user is root admin (role ID = 1)
        const adminRoles = payload.adminRoles || [];
        const isRoot = adminRoles.some((role: any) => role.id === 1);
        state.isRootAdmin = isRoot;
        
        if (isRoot) {
          localStorage.setItem("cmt_admin", "1");
        }
        //set for navbar fullname
        const fullname = `${payload.firstname} ${payload.lastname}`;
        state.cmt_fullname = fullname;
        state.currentUser = fullname;
        localStorage.setItem("cmt_fullname", fullname);
      }
    },
    clearUser: (state) => {
      state.cmt_fullname = "";
      state.cmt_token = "";
      state.currentUser = "";
      state.isRootAdmin = false;
      state.isLogged = false;
      state.user = null;
      localStorage.removeItem("cmt_token");
      localStorage.removeItem("cmt_fullname");
      localStorage.removeItem("cmt_admin");
      localStorage.removeItem("cmt_require_password_change");
    },
  },
});

export const { setTitle, setUser, setProfile, setTitleWithBack, clearUser } = appStore.actions;

export const selectTitle = (state: RootState) => state.user.title;
export const selectShowBtnBack = (state: RootState) => state.user.showBtnBack;
export const selectBackToPath = (state: RootState) => state.user.backToPath;
export const selectCurrentUser = (state: RootState) => state.user.currentUser;
export const selectRootAdmin = (state: RootState) => {
  // Check if user is root admin from the Redux state
  const reduxIsRoot = state.user.isRootAdmin;
  
  // Fallback: check from localStorage
  const localStorageAdmin = localStorage.getItem("cmt_admin");
  const localStorageIsRoot = localStorageAdmin === "1";
  
  // Return true if either Redux state or localStorage indicates root admin
  return reduxIsRoot || localStorageIsRoot;
};
export const selectIsLogged = (state: RootState) => state.user.isLogged;
export default appStore.reducer;
